import { nowIsoAndUnix } from "@@/shared/date-time/iso-and-unix";
import { contactInfoZodObject } from "@@/shared/locations/contact-info";
import { priceZodSchema } from "@@/shared/prices/price";
import { translatableZodSchema } from "@@/shared/translations/translation";
import { systemUserZodSchema } from "@@/shared/users/system-user";
import { userIdZodSchema } from "@@/shared/users/user-id";
import type { Except } from "type-fest";
import { z } from "zod";
import type { PayRequestId} from "../pay-request-id";
import { payRequestIdFactory } from "../pay-request-id";
import { payRequestItemZodSchema } from "../pay-request-item";
import { payRequestPriceCalculator } from "../pay-request-price-calculator";
import { payRequestRecipientZodSchema } from "../pay-request-recipient";
import { payRequestRequesterZodSchema } from "../pay-request-requester";
import type { PayRequestEvent } from "./_pay-request.event";
import type {
    PayRequestEventId} from "./_pay-request.event-id";
import {
    payRequestEventIdFactory,
} from "./_pay-request.event-id";
import { payRequestEventSchemaFactory } from "./_pay-request.event.schema-factory";

const payRequestCreatedEventZodObject = payRequestEventSchemaFactory(
    "PAY_REQUEST_CREATED_EVENT",
    z.object({
        _createdBy: userIdZodSchema.or(systemUserZodSchema),
        description: translatableZodSchema.optional(),
        /** Items to be paid using the pay request */
        items: z.array(payRequestItemZodSchema).min(1),
        /** Pay request amount */
        price: priceZodSchema,
        /**
         * Contact info for payers of the pay request
         * a notification will (have) be(en) sent to these contacts
         */
        recipients: z.array(payRequestRecipientZodSchema).min(1),
        requester: payRequestRequesterZodSchema,
        type: z.literal("PAY_REQUEST_CREATED_EVENT"),
        orderContactInfo: contactInfoZodObject,
    }),
);

type PayRequestCreatedEvent = z.infer<typeof payRequestCreatedEventZodObject>;
const payRequestCreatedEventZodSchema: z.ZodSchema<PayRequestCreatedEvent> =
    payRequestCreatedEventZodObject;

type PayRequestCreatedEventFactoryParams = Pick<PayRequestEvent, "paymentFor"> &
    PayRequestCreatedEvent["data"];
const payRequestCreatedEventFactory = (
    params: Except<PayRequestCreatedEventFactoryParams, "price" | "type"> & {
        _id?: PayRequestEventId;
        payRequestId?: PayRequestId;
    },
) => {
    const now = nowIsoAndUnix();
    const event: PayRequestCreatedEvent = {
        _id: params._id ?? payRequestEventIdFactory(),
        _type: "PAY_REQUEST_EVENT",
        _createdAt: now,
        _happenedAt: now,
        _version: 1,
        type: "PAY_REQUEST_CREATED_EVENT",
        data: {
            ...params,
            price: payRequestPriceCalculator(params.items),
            type: "PAY_REQUEST_CREATED_EVENT",
        },
        payRequestId: params.payRequestId ?? payRequestIdFactory(),
        paymentFor: params.paymentFor,
    };
    // console.log("💜💜💜 PAY REQ CREATED EVENT DATA ITEMS", event.data.items);
    return payRequestCreatedEventZodSchema.parse(event);
};

const isPayRequestCreatedEvent = (
    item: PayRequestEvent,
): item is PayRequestCreatedEvent => {
    return item.type === "PAY_REQUEST_CREATED_EVENT";
};

export {
    isPayRequestCreatedEvent,
    payRequestCreatedEventFactory,
    payRequestCreatedEventZodObject,
    payRequestCreatedEventZodSchema,
};
export type { PayRequestCreatedEvent };
